import parse from "node-html-parser";
import ReactHtmlParser from "@/src/utils/html-parser";
import {
  FONT_TEXT,
  FONT_UI,
} from "@/src/components/common/broadsheet/tokens";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

const parseStyle = (styleString: string) => {
  return styleString.split(";").reduce(
    (acc, rule) => {
      const [property, value] = rule.split(":").map((item) => item.trim());
      if (property && value) {
        acc[property.replace(/-./g, (match) => match[1].toUpperCase())] = value;
      }
      return acc;
    },
    {} as Record<string, string>,
  );
};

const parseTableContent = (htmlContent: string) => {
  const root = parse(htmlContent);
  const table = root.querySelector("table");
  if (!table) return null;

  const headers = table.querySelectorAll("thead tr").map((tr: any) =>
    tr.querySelectorAll("th").map((td: any) => ({
      content: td.innerHTML,
      rowSpan: parseInt(td.getAttribute("rowspan") || "1"),
      colSpan: parseInt(td.getAttribute("colspan") || "1"),
    })),
  );

  const rows = table.querySelectorAll("tbody tr").map((tr: any) =>
    tr.querySelectorAll("td,th").map((td: any) => ({
      component: td.tagName.toLowerCase(),
      content: td.innerHTML,
      rowSpan: parseInt(td.getAttribute("rowspan") || "1"),
      colSpan: parseInt(td.getAttribute("colspan") || "1"),
      style: td.getAttribute("style"),
    })),
  );

  return { headers, rows };
};

const TableContent = ({
  content,
  label,
}: {
  content: string;
  label?: string | null;
}) => {
  const tableData = parseTableContent(content);
  if (!tableData) return null;

  return (
    <TableContainer
      component={Box}
      tabIndex={0}
      role="region"
      aria-label={label ? `${label}, scrollable` : "Table, scrollable"}
      sx={{
        overflowX: "auto",
        my: 1,
        border: "1px solid",
        borderColor: "custom.ruleHairline",
        backgroundColor: "custom.panelSurface",
      }}
    >
      <Table
        sx={{
          borderCollapse: "collapse",
          fontFamily: FONT_TEXT,
          backgroundColor: "transparent",
          "& th, & td": {
            border: 0,
            fontFamily: "inherit",
            fontSize: "0.8125rem",
            lineHeight: 1.5,
            color: "custom.ink",
            px: 0.75,
            py: 0.4375,
            fontVariantNumeric: "tabular-nums",
            verticalAlign: "baseline",
            backgroundColor: "transparent",
          },
          "& thead th": {
            fontFamily: FONT_UI,
            fontSize: "0.6875rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "custom.inkMuted",
            whiteSpace: "nowrap",
            pt: 0.625,
            pb: 0.4375,
          },
          "& thead tr:last-of-type th": {
            borderBottom: "1px solid",
            borderBottomColor: "custom.ink",
          },
          "& tbody tr:not(:last-of-type) th, & tbody tr:not(:last-of-type) td":
            {
              borderBottom: "1px solid",
              borderBottomColor: "custom.ruleHairline",
            },
          "& tbody tr:first-of-type th, & tbody tr:first-of-type td": {
            pt: 0.5625,
          },
          "& tbody tr:last-of-type th, & tbody tr:last-of-type td": {
            pb: 0.5625,
          },
        }}
      >
        <TableHead>
          {tableData.headers.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell: any, cellIndex: number) => (
                <TableCell
                  key={cellIndex}
                  rowSpan={cell.rowSpan}
                  colSpan={cell.colSpan}
                  sx={{ verticalAlign: "bottom" }}
                >
                  {ReactHtmlParser(cell.content)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {tableData.rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell: any, cellIndex: number) => (
                <TableCell
                  component={cell.component}
                  key={cellIndex}
                  rowSpan={cell.rowSpan}
                  colSpan={cell.colSpan}
                  style={{
                    verticalAlign: "bottom",
                    ...parseStyle(cell.style || ""),
                  }}
                >
                  {ReactHtmlParser(cell.content)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableContent;
